import React, { useEffect, type FC, useState, useMemo } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import DecisionMakingHeader from './DecisionMakingHeader';
import NoListSortsModal from './NoListSortsModal';
import { Provider, useDispatch } from 'react-redux';
import { AppDispatch, store } from '../../redux/store';
import { enableMapSet } from 'immer';
enableMapSet();
import DecisionMakingContainer from './DecisionMakingContainer';
import { FilteringProvider } from '../../contexts/Filtering';
import { AppSettingsProvider } from '../../contexts/AppSettings';
import DecisionMakingPropertySidebar from './DecisionMakingPropertySidebar';
import { LoadingSpinner } from '../icons/OurIcons';
import useSessionStorage from '../../hooks/useSessionStorage';
import { ToastProvider } from '../../contexts/ToastContext';
import { DecisionFormProvider } from '../../contexts/DecisionMaking/DecisionFormContext';
import { RepairCostProvider } from '../../contexts/DecisionMaking/RepairCostContext';
import { PropertyProvider } from '../../contexts/DecisionMaking/PropertyContext';
import { AvmProvider } from '../../contexts/DecisionMaking/AvmContext';
import { ApproachProvider } from '../../contexts/DecisionMaking/ApproachContext';
import { ListSortProvider, useListSortContext } from '../../contexts/DecisionMaking/ListSortContext';
import { setMapKey } from '../../redux/state/mapSlice';
import { fetchColumns } from '../../redux/state/columnsSlice';
import { setStreetViewKey } from '../../redux/state/streetViewSlice';

export type DecisionMakingViewerProps = {
  mapKey: string;
  streetViewKey: string;
  incomingListSortId: string;
}

const queryClient = new QueryClient();

const DecisionMakingViewer: FC<DecisionMakingViewerProps> = ({ mapKey, streetViewKey, incomingListSortId }) => {
  const { storedValue: activeSessionListSortId, setValue: setActiveSessionListSortId } = useSessionStorage('activeListSort', incomingListSortId);

  useEffect(() => {
    // NOTE: This is the same issue as in FinalDecisionMakingViewer.tsx
    //    this solution is because our navbar is set up in the ruby controller and doesnt have context of front end session
    // before we render the component, we need to check if the incomingListSortId is present in the url
    // if it is, we need to set the activeSessionListSortId to the incomingListSortId
    // if it is not, we need to set the activeSessionListSortId to the activeSessionListSortId in session storage
    // we need to do this because the incomingListSortId is not always present in the url
    // and we need to ensure that the activeSessionListSortId is always present in the url
    const url = new URL(window.location.href);
    if (!incomingListSortId && activeSessionListSortId) {
      const isSlashAtEnd = url.pathname.endsWith('/');
      // If we have an active list sort id, but no incoming list sort id, we need to set the url param to the active list sort id
      window.location.href = url.toString() + `${isSlashAtEnd ? '' : '/'}${activeSessionListSortId}`;
    }
  }, []);

  if (!incomingListSortId && activeSessionListSortId) {
    return (
      <div id='DecisionMakingViewer-loading-spinner-container' className='flex flex-col justify-center items-center h-screen'>
        <LoadingSpinner size='12' />
        <p className='text-md text-gray-500'>Loading Decision Making content...</p>
      </div>
    );
  }

  return (
    <Provider store={store}>
      <QueryClientProvider client={queryClient}>
        <ToastProvider>
          <FilteringProvider>
            <AppSettingsProvider>
              <DecisionFormProvider>
                <ListSortProvider incomingListSortId={incomingListSortId}>
                  <PropertyProvider>
                    <AvmProvider>
                      <RepairCostProvider>
                        <ApproachProvider>
                          <DecisionMakingViewerChild mapKey={mapKey} streetViewKey={streetViewKey}/>
                        </ApproachProvider>
                      </RepairCostProvider>
                    </AvmProvider>
                  </PropertyProvider>
                </ListSortProvider>
              </DecisionFormProvider>
            </AppSettingsProvider>
          </FilteringProvider>
        </ToastProvider>
      </QueryClientProvider>
    </Provider>
  );
};

const DecisionMakingViewerChild: FC<{mapKey: string, streetViewKey: string}> = ({ mapKey, streetViewKey }) => {
  const dispatch = useDispatch<AppDispatch>();
  const [ isSidebarOpen, setIsSidebarOpen ] = useState(false);
  const { navigateToSelectedListSort } = useListSortContext();

  useEffect(() => {
    dispatch(setMapKey(mapKey));
    dispatch(setStreetViewKey(streetViewKey));
    dispatch(fetchColumns());
  }, []);

  // TODO: Replace with actual hooks from new context once listSortContext is created
  const {
    activeListSort,
    listSorts,
    isListSortsLoading,
  } = useListSortContext();

  if (isListSortsLoading) {
    return (
      <div className='flex flex-col justify-center items-center h-screen'>
        <LoadingSpinner size='12' />
        <p className='text-md text-gray-500'>Loading list content...</p>
      </div>
    );
  }

  if (!listSorts?.length) {
    return (
      <div className='w-screen content-center px-10'>
        <div className='mx-auto mt-36 px-96 w-5/6'>
          <NoListSortsModal />
        </div>
      </div>
    );
  }

  const DecisionMakingInvalidStates = () => {
    if (!activeListSort) {
      return (
        <div className='flex flex-row justify-center p-5'>
          <div className='text-center'>
            <p className='text-xl'>Please select a list sort.</p>
          </div>
        </div>
      );
    }

    if (!activeListSort?.total_properties) {
      return (
        <div className='w-screen content-center px-10'>
          <div className='mx-auto mt-36 px-96 w-5/6'>
            <div className='text-center'>
              <p className='text-2xl'>No properties found for this list sort</p>
            </div>
          </div>
        </div>
      );
    }
  };

  const activeListSortHasProperties = activeListSort?.total_properties;

  return (
    <div id='DecisionMakingViewer-container' className='w-screen max-h-full top-0 pb-10 text-xs justify-center'>
      <DecisionMakingHeader
        title='Decision Making'
        activeListSort={activeListSort}
        listSorts={listSorts}
        navigateToSelectedListSort={navigateToSelectedListSort}
      />
      <div id='DecisionMakingViewer-portal-container' className="flex w-full h-8 mt-2">
        <div id="portal-container-arrow-icons" className="w-10 flex items-center justify-center"></div> {/* Left-hand arrow container (fixed size) */}
        <div id="portal-container-list-sidebar" className="flex-grow flex justify-center"></div> {/* Portal container (takes up remaining space and centers content) */}
      </div>
      {!activeListSortHasProperties ? (
        <DecisionMakingInvalidStates />
      ) : (
        <div className='flex flex-row'>
          <div className={`${isSidebarOpen && 'w-1/6 min-w-[250px]'} max-w-[250px]`}>
            <DecisionMakingPropertySidebar
              isSidebarOpen={isSidebarOpen}
              setIsSidebarOpen={setIsSidebarOpen}
            />
          </div>
          <div className='w-full'>
            <DecisionMakingContainer />
          </div>
        </div>
      )}
    </div>
  );
};

export default DecisionMakingViewer;
